/**
 * Horizontal Explore chip-strip that lives directly below the TaglineBanner.
 * Replaces the previous dropdown menu that was crowding the top navigation.
 * Pulls the same admin-managed `nav_categories` collection so it stays in sync
 * with the mobile drawer + admin UI.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { ChevronLeft, ChevronRight, Compass } from "lucide-react";
import CountryPicker from "@/components/CountryPicker";

// When Explore is shown on the Food Pulse child site, we replace the
// generic SocialPulse nav-categories with direct links into the /m/*
// mobile web-app screens we are actively developing. Same override
// used by ExploreMenu (the top-right dropdown) so both surfaces stay
// consistent.
const FOODPULSE_EXPLORE_BAR = [
  { id: "m-home",       label: "Home",              href: "/m" },
  { id: "m-products",   label: "Products",          href: "/m/products" },
  { id: "m-recipes",    label: "Recipes",           href: "/m/recipes" },
  { id: "m-deals",      label: "Deals & Coupons",   href: "/m/deals" },
  { id: "m-stores",     label: "Stores near me",    href: "/m/stores" },
  { id: "m-news",       label: "News & Events",     href: "/m/news" },
  { id: "m-groups",     label: "Groups",            href: "/m/groups" },
  { id: "m-charities",  label: "Charities",         href: "/m/charities" },
  { id: "m-my-lists",   label: "My Lists",          href: "/m/lists" },
  { id: "m-add-recipe", label: "Submit a Recipe",   href: "/m/add-recipe" },
  { id: "m-add-deal",   label: "Submit a Deal",     href: "/m/add-deal" },
];

function detectFoodPulse() {
  if (typeof window === "undefined") return false;
  const host = (window.location.hostname || "").toLowerCase();
  if (host.includes("foodpulse")) return true;
  const path = (window.location.pathname || "").toLowerCase();
  return path.startsWith("/food-pulse") || path.startsWith("/m");
}

export default function ExploreBar() {
  const [dbItems, setDbItems] = useState([]);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const scrollerRef = useRef(null);
  const nav = useNavigate();

  const isFoodPulse = useMemo(detectFoodPulse, []);
  const items = isFoodPulse ? FOODPULSE_EXPLORE_BAR : dbItems;

  useEffect(() => {
    if (isFoodPulse) return; // Food Pulse uses the hard-coded mobile list.
    http.get("/nav-categories").then((r) => setDbItems(r.data.items || [])).catch(() => {});
  }, [isFoodPulse]);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 4);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [items]);

  const scrollBy = (delta) => {
    scrollerRef.current?.scrollBy({ left: delta, behavior: "smooth" });
  };

  const go = (href) => {
    if (!href) return;
    if (/^https?:\/\//i.test(href)) window.open(href, "_blank", "noopener,noreferrer");
    else nav(href);
  };

  if (items.length === 0) return null;

  return (
    <div
      data-testid="explore-bar"
      className="w-full border-b"
      style={{ background: "var(--wofy-bg)", borderColor: "var(--wofy-border)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-2.5 flex items-center gap-3">
        {/* Label */}
        <div
          className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider shrink-0 pr-3 border-r"
          style={{ color: "var(--wofy-text-soft)", borderColor: "var(--wofy-border)" }}
        >
          <Compass className="w-3.5 h-3.5" style={{ color: "var(--wofy-primary)" }} />
          Explore
        </div>

        {/* Scrollable chips */}
        <div className="relative flex-1 min-w-0">
          {showLeft && (
            <button
              type="button"
              aria-label="Scroll left"
              data-testid="explore-bar-scroll-left"
              onClick={() => scrollBy(-240)}
              className="hidden md:flex absolute -left-2 top-1/2 -translate-y-1/2 z-10 w-7 h-7 items-center justify-center rounded-full shadow-md border"
              style={{ background: "var(--wofy-bg)", borderColor: "var(--wofy-border)" }}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
          <ul
            ref={scrollerRef}
            className="flex items-center gap-1.5 overflow-x-auto scroll-smooth whitespace-nowrap"
            style={{ scrollbarWidth: "none" }}
          >
            <style>{`[data-testid="explore-bar"] ul::-webkit-scrollbar { display: none; }`}</style>
            {items.map((it) => (
              <li key={it.id} className="shrink-0">
                <button
                  type="button"
                  data-testid={`explore-bar-item-${it.id}`}
                  onClick={() => go(it.href)}
                  className="px-3 py-1.5 text-xs rounded-full border hover:bg-[color:var(--wofy-surface)] transition-colors"
                  style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text)" }}
                >
                  {it.label}
                </button>
              </li>
            ))}
          </ul>
          {showRight && (
            <button
              type="button"
              aria-label="Scroll right"
              data-testid="explore-bar-scroll-right"
              onClick={() => scrollBy(240)}
              className="hidden md:flex absolute -right-2 top-1/2 -translate-y-1/2 z-10 w-7 h-7 items-center justify-center rounded-full shadow-md border"
              style={{ background: "var(--wofy-bg)", borderColor: "var(--wofy-border)" }}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Country picker — visible on every page, in the strip below the tagline */}
        <div
          className="shrink-0 pl-3 border-l flex items-center gap-2"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="explore-bar-country"
        >
          <span
            className="hidden md:inline text-[10px] font-mono uppercase tracking-wider whitespace-nowrap"
            style={{ color: "var(--wofy-text-soft)" }}
            data-testid="country-picker-caption"
          >
            Select a Country
          </span>
          <CountryPicker />
        </div>
      </div>
    </div>
  );
}
